import { FC, useEffect, useState } from "react";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import type { CrudColumnDef } from "@/components/tables/crudColumnDef";
import { useTranslation } from "react-i18next";
import { replaceDate } from "@/helper/dateHandling";
import { FieldOption } from "@/components/general/genericJsonBuilder";
import { api } from "@/helper/api";
import RestrictedPage from "@/consumer/restrictedPage";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { KAER_COMPANY_SUBJECT_ID } from "@/helper/systemIds";

const TrackedServiceExaminationTemplatesTable: FC = () => {
  const { t } = useTranslation();
  usePageHeading(t("trackedservice_examinations_tableheading"));
  const [kaerEmployeeForFiltering, setKaerEmployeeForFiltering] = useState<
    any[]
  >([]);

  useEffect(() => {
    getKaerEmployees(setKaerEmployeeForFiltering);
  }, []);

  const conditionsFieldOptions: FieldOption[] = [
    {
      key: "*",
      type: "string",
      operators: [
        "eq",
        "neq",
        "gt",
        "lt",
        "appointment_has_category",
        "appointment_has_examination",
        "appointment_has_probe",
        "appointment_measurement_is_yes",
        "appointment_measurement_is_no",
        "appointment_measurement_with_name_has_value"
      ],
      operator_aliases: {
        eq: "eq =",
        neq: "neq ≠",
        gt: "gt >",
        lt: "lt <",
      },
      renderKeyAs: "textfield",
      allow_custom_input: true,
      renderValueInputTypeToggle: true,
      valueTypesForInputToggle: ["string", "number", "bool"],
      operatorsExpectingStringValuesOnly: [
        "appointment_has_category",
        "appointment_has_examination",
        "appointment_has_probe",
        "appointment_measurement_is_yes",
        "appointment_measurement_is_no"
      ],
      operatorsExpectingObjectValues: {
        appointment_measurement_with_name_has_value: [
          { key: "name", type: "string" },
          { key: "value", type: "string" },
        ],
      },
      fixedKeyOperatorGroups: [
        {
          fixedKey: "entity",
          operators: [
            "appointment_has_category",
            "appointment_has_examination",
            "appointment_has_probe",
            "appointment_measurement_is_yes",
            "appointment_measurement_is_no",
            "appointment_measurement_with_name_has_value"
          ]
        }
      ],
    },
  ];

  const trackedServiceExaminationTemplateColumns: CrudColumnDef[] = [
    { header: t("id"), accessorKey: "id", id: "id" },
    {
      header: t("examination"),
      accessorKey: "examination",
      id: "examination",
      entity: "examination",
      asyncEntitySearch: true,
      customColumnFilterId: "examination__name",
      accessorFn: (row: any) => row.examination.name,
      customSortingKey: "examination__name",
      enableForCreate: true,
      required: true,
      errorMessage: t("field_required"),
    },
    {
      header: t("trackedservice_template"),
      accessorKey: "tracked_service_template",
      id: "tracked_service_template",
      entity: "trackedServiceTemplate",
      asyncEntitySearch: true,
      customColumnFilterId: "tracked_service_template__internal_name",
      Cell: ({ cell }: any) => {
        return <span>{cell.getValue().name}</span>;
      },
      enableForCreate: true,
      required: true,
      errorMessage: t("field_required"),
    },
    {
      header: t("conditions"),
      accessorKey: "conditions",
      id: "conditions",
      customColumnFilterId: "conditions",
      enableSorting: false,
      enableForCreate: true,
      required: true,
      errorMessage: t("field_required"),
      useJsonBuilder: true,
      jsonBuilderProps: {
        required: true,
        fieldOptions: conditionsFieldOptions,
        enableAddNewEntryButton: true,
        enableDeleteButton: true,
        isArrayOfObjects: true,
        emptyArrayOfObjectsValue: "singleEmptyObject",
      },
      accessorFn: (row: any) => {
        const conditions = [];
        if (row.conditions) {
          conditions.push(JSON.stringify(row.conditions));
        }

        return conditions.join(", ");
      },
    },

    {
      header: t("created_on"),
      accessorKey: "created_on",
      id: "created_on",
      filterVariant: "date",
      sortingFn: "datetime",

      Cell: ({ cell }: any) => {
        return <span>{replaceDate(cell.getValue())}</span>;
      },
    },
    {
      header: t("created_by"),
      accessorKey: "created_by",
      filterVariant: "select",
      filterSelectOptions: kaerEmployeeForFiltering,

      getOnlyActiveUsers: {
        status: true,
        companySubjectId: KAER_COMPANY_SUBJECT_ID,
      },
      id: "created_by",
      Cell: ({ cell }: any) => {
        return <span>{cell.getValue()?.name}</span>;
      },
    },
    {
      header: t("modified_on"),
      accessorKey: "modified_on",
      filterVariant: "date",
      id: "modified_on",
      sortingFn: "datetime",
      Cell: ({ cell }: any) => {
        return <span>{replaceDate(cell.getValue())}</span>;
      },
    },
    {
      header: t("modified_by"),
      accessorKey: "modified_by",
      filterVariant: "select",
      filterSelectOptions: kaerEmployeeForFiltering,
      // enableColumnFilter: false,

      getOnlyActiveUsers: {
        status: true,
        companySubjectId: KAER_COMPANY_SUBJECT_ID,
      },
      id: "modified_by",
      Cell: ({ cell }: any) => {
        return <span>{cell.getValue()?.name}</span>;
      },
    },
  ];
  return (
    <RestrictedPage permissionRequired="frontend_view_trackedservice_examination_templates">
      <GenericCrudTable
        entity="examinationTrackedServiceTemplate"
        columns={trackedServiceExaminationTemplateColumns}
        remountCrudModalOnPresetChange
        disableGlobalFilter
        allowColumnFiltering
        showColumnFilterbyDefault
      />
    </RestrictedPage>
  );
};

export default TrackedServiceExaminationTemplatesTable;

function getKaerEmployees(setOptions: any) {
  const additionalUserParameters: any = {};
  additionalUserParameters["impersonate_subject"] = KAER_COMPANY_SUBJECT_ID;
  additionalUserParameters["is_active"] = "true";
  additionalUserParameters["include_relations"] = true;

  api.genericApiRequest({
    entity: "user",
    method: "get",
    parametersToRender: {
      additionalUrlParameters: additionalUserParameters,
      depth: "0",
    },

    successHandler: (res: any) => {
      const employeeOptions = res.data.results.map((item: any) => ({
        value: item.person.user,
        label: item.last_name + ", " + item.first_name,
      }));

      setOptions(
        employeeOptions.sort((a: any, b: any) => a.label.localeCompare(b.label))
      );
    },
  });
}
